import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Alert,
  Button,
  Tooltip,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import {
  Send as SendIcon,
  SmartToy as AIIcon,
  Person as UserIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Language as ExternalIcon,
  Storage as InternalIcon,
  PersonAdd as ImportIcon,
  Public as WebIcon,
  Schedule as ScheduleIcon,
  Flag as PriorityIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as SuccessIcon,
  Description as DocumentIcon,
  Article as ArticleIcon,
  Add as AddIcon,
  AttachFile as AttachmentIcon,
  MarkEmailRead as ReadIcon,
  MarkEmailUnread as UnreadIcon,
  Star as StarIcon,
  AddCircleOutline as NewChatIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Search as SearchIcon,
  Help as HelpIcon,
  Assignment as TicketIcon,
  Inventory as ArchiveIcon,
  MailOutline as MailIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { aiChatApi, adressenApi, dokumenteApi, speechApi, ticketsApi, benutzerApi } from '../services/api';
import { milestoneService, type Milestone } from '../services/milestoneService';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TicketDialog from '../components/TicketDialog';
import type { Ticket, Benutzer, Adresse } from '../types/api';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  messageType?: string;
  data?: any;
  timestamp: Date;
}

interface AddressResult {
  id?: number | string;
  name: string;
  firma?: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  land?: string;
  telefon?: string;
  email?: string;
  source?: string;
  isInternal?: boolean;
  confidence?: number;
  displayAddress?: string;
}

const CHAT_STORAGE_KEY = 'skeena_ai_chat_history';

// Verfügbare Befehle für die Sidebar
interface CommandCategory {
  title: string;
  icon: React.ReactNode;
  commands: { label: string; template: string; description: string }[];
}

const COMMAND_CATEGORIES: CommandCategory[] = [
  {
    title: 'Wissenssuche',
    icon: <SearchIcon fontSize="small" />,
    commands: [
      { label: 'Was weisst du über...', template: 'Was weisst du über ', description: 'Durchsucht alle Quellen' },
      { label: 'Informationen zu...', template: 'Informationen zu ', description: 'Sucht relevante Infos' },
      { label: 'Was haben wir mit...', template: 'Was haben wir mit ', description: 'Findet Kundendaten' },
    ],
  },
  {
    title: 'Dokumente',
    icon: <DocumentIcon fontSize="small" />,
    commands: [
      { label: 'Gibt es ein Dokument zu...', template: 'Gibt es ein Dokument zu ', description: 'Sucht lokal + Archiv' },
      { label: 'Finde das Dokument...', template: 'Finde das Dokument ', description: 'Dokumentensuche' },
      { label: 'Zeige alle Dokumente', template: 'Zeige alle Dokumente von heute', description: 'Listet Dokumente' },
      { label: 'Erstelle einen Brief an...', template: 'Erstelle einen Brief an ', description: 'Neues Dokument' },
      { label: 'Lade Dokument hoch', template: 'Lade ein Dokument hoch', description: 'Upload starten' },
    ],
  },
  {
    title: 'E-Mails',
    icon: <EmailIcon fontSize="small" />,
    commands: [
      { label: 'Zeige E-Mails der letzten...', template: 'Zeige E-Mails der letzten 7 Tage', description: 'E-Mail-Liste' },
      { label: 'Ungelesene E-Mails', template: 'Zeige alle ungelesenen E-Mails', description: 'Nur ungelesene' },
      { label: 'E-Mails von...', template: 'Zeige E-Mails von ', description: 'Nach Absender filtern' },
    ],
  },
  {
    title: 'Adressen',
    icon: <BusinessIcon fontSize="small" />,
    commands: [
      { label: 'Adresse von...', template: 'Zeige mir die Adresse von ', description: 'Sucht intern + extern' },
      { label: 'Telefonnummer von...', template: 'Wie ist die Telefonnummer von ', description: 'Kontaktdaten' },
      { label: 'E-Mail-Adresse von...', template: 'Wie lautet die E-Mail-Adresse von ', description: 'E-Mail finden' },
    ],
  },
  {
    title: 'Tickets',
    icon: <TicketIcon fontSize="small" />,
    commands: [
      { label: 'Erstelle Ticket...', template: 'Erstelle ein Ticket: ', description: 'Neues Ticket anlegen' },
      { label: 'Ticket mit Frist', template: 'Erstelle ein Ticket: , dringend, bis morgen', description: 'Mit Fälligkeit' },
    ],
  },
  {
    title: 'Hilfe',
    icon: <HelpIcon fontSize="small" />,
    commands: [
      { label: 'Was kannst du?', template: 'Was kannst du?', description: 'Zeigt alle Funktionen' },
      { label: 'Hilfe', template: 'Hilfe', description: 'Allgemeine Hilfe' },
    ],
  },
];

const defaultWelcomeMessage: ChatMessage = {
  id: '1',
  role: 'assistant',
  content: 'Hallo! Ich bin Ihr Skeena-Assistent. Wie kann ich Ihnen helfen?\n\nSie können mich z.B. fragen:\n- "Zeige mir die Adresse von Müller AG"\n- "Erstelle ein Ticket: Rechnung nachfassen, dringend"\n- "Finde das Angebot für Meier AG"\n- "Erstelle einen Brief an Müller AG"\n- "Lade ein Dokument hoch"',
  messageType: 'text',
  timestamp: new Date(),
};

// Lade Chat-Verlauf aus localStorage
const loadChatHistory = (): ChatMessage[] => {
  try {
    const saved = localStorage.getItem(CHAT_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Konvertiere timestamp-Strings zurück zu Date-Objekten
      return parsed.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));
    }
  } catch (e) {
    console.warn('Fehler beim Laden des Chat-Verlaufs:', e);
  }
  return [defaultWelcomeMessage];
};

// Speichere Chat-Verlauf in localStorage
const saveChatHistory = (messages: ChatMessage[]) => {
  try {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
  } catch (e) {
    console.warn('Fehler beim Speichern des Chat-Verlaufs:', e);
  }
};

const AIChat: React.FC = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>(loadChatHistory);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isUploading, setIsUploading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [speechMode, setSpeechMode] = useState<'whisper' | 'webSpeech' | null>(null);

  // Ticket Dialog State
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [ticketUsers, setTicketUsers] = useState<Benutzer[]>([]);
  const [ticketMilestones, setTicketMilestones] = useState<Milestone[]>([]);
  const [ticketAdressen, setTicketAdressen] = useState<Adresse[]>([]);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    'Wissenssuche': true,
    'Dokumente': true,
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const webSpeechRecognitionRef = useRef<any>(null);

  // MediaRecorder Setup - Check for browser support AND Whisper service availability
  // Falls Whisper nicht verfügbar ist, Web Speech API als Fallback verwenden
  useEffect(() => {
    const checkSpeechSupport = async () => {
      // Prüfe ob MediaRecorder und getUserMedia verfügbar sind (für Whisper)
      const browserSupport = !!(
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia === 'function' &&
        typeof window.MediaRecorder === 'function'
      );

      // Prüfe ob Web Speech API verfügbar ist (Fallback)
      const webSpeechSupport = !!(
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
      );

      // Prüfe ob Whisper Service erreichbar ist
      try {
        const response = await speechApi.health();
        if (response.data.status === 'healthy' && browserSupport) {
          console.log('Speech: Whisper Service verfügbar', response.data.whisperService);
          setSpeechMode('whisper');
          setSpeechSupported(true);
          return;
        }
      } catch (error) {
        console.log('Speech: Whisper Service nicht erreichbar');
      }

      // Fallback auf Web Speech API
      if (webSpeechSupport) {
        console.log('Speech: Web Speech API als Fallback aktiviert (Browser-basiert)');
        setSpeechMode('webSpeech');
        setSpeechSupported(true);
        return;
      }

      console.log('Speech: Keine Spracherkennung verfügbar');
      setSpeechSupported(false);
    };
    checkSpeechSupport();
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stream stoppen
        stream.getTracks().forEach(track => track.stop());

        // Audio-Blob erstellen
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

        // Nur transkribieren wenn genügend Audio aufgenommen wurde
        if (audioBlob.size > 1000) {
          setIsTranscribing(true);
          try {
            const response = await speechApi.transcribe(audioBlob, 'de');
            if (response.data.success && response.data.text) {
              setInput(response.data.text);
            } else {
              setSnackbar({
                open: true,
                message: 'Transkription fehlgeschlagen - bitte erneut versuchen',
                severity: 'error'
              });
            }
          } catch (error: any) {
            console.error('Transcription error:', error);
            setSnackbar({
              open: true,
              message: error.response?.status === 503
                ? 'Whisper Service nicht erreichbar'
                : 'Fehler bei der Transkription',
              severity: 'error'
            });
          } finally {
            setIsTranscribing(false);
          }
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error: any) {
      console.error('Microphone access error:', error);
      setSnackbar({
        open: true,
        message: 'Mikrofon-Zugriff wurde verweigert. Bitte erlauben Sie den Zugriff in den Browser-Einstellungen.',
        severity: 'error'
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Web Speech API - Start
  const startWebSpeechRecording = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSnackbar({
        open: true,
        message: 'Web Speech API wird von diesem Browser nicht unterstützt',
        severity: 'error'
      });
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'de-DE';
    recognition.continuous = true;
    recognition.interimResults = true;

    let finalTranscript = '';
    let interimTranscript = '';

    recognition.onstart = () => {
      console.log('Web Speech: Aufnahme gestartet');
      setIsRecording(true);
    };

    recognition.onresult = (event: any) => {
      interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      // Zeige Zwischenergebnisse live im Input
      setInput(finalTranscript + interimTranscript);
    };

    recognition.onerror = (event: any) => {
      console.error('Web Speech Error:', event.error);
      setIsRecording(false);
      if (event.error === 'not-allowed') {
        setSnackbar({
          open: true,
          message: 'Mikrofon-Zugriff verweigert. Bitte in Browser-Einstellungen erlauben.',
          severity: 'error'
        });
      } else if (event.error !== 'aborted') {
        setSnackbar({
          open: true,
          message: `Spracherkennung-Fehler: ${event.error}`,
          severity: 'error'
        });
      }
    };

    recognition.onend = () => {
      console.log('Web Speech: Aufnahme beendet');
      setIsRecording(false);
      // Finalen Text setzen
      if (finalTranscript.trim()) {
        setInput(finalTranscript.trim());
      }
    };

    webSpeechRecognitionRef.current = recognition;
    recognition.start();
  };

  // Web Speech API - Stop
  const stopWebSpeechRecording = () => {
    if (webSpeechRecognitionRef.current) {
      webSpeechRecognitionRef.current.stop();
      webSpeechRecognitionRef.current = null;
    }
    setIsRecording(false);
  };

  // Toggle Recording - wählt automatisch Whisper oder Web Speech
  const toggleRecording = () => {
    if (isRecording) {
      // Stop
      if (speechMode === 'whisper') {
        stopRecording();
      } else {
        stopWebSpeechRecording();
      }
    } else {
      // Start
      if (speechMode === 'whisper') {
        startRecording();
      } else {
        startWebSpeechRecording();
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Speichere Chat-Verlauf bei Änderungen
  useEffect(() => {
    saveChatHistory(messages);
  }, [messages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await aiChatApi.sendMessage(input);
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.message,
        messageType: response.data.messageType,
        data: response.data.data,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        messageType: 'error',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    // Chat-Verlauf löschen und zurücksetzen
    localStorage.removeItem(CHAT_STORAGE_KEY);
    setMessages([defaultWelcomeMessage]);
    setInput('');
    setExpandedCards({});
  };

  // Ticket Dialog öffnen
  const handleOpenTicketDialog = async (ticketId: number) => {
    setTicketLoading(true);
    try {
      // Parallel laden: Ticket, Benutzer, Meilensteine, Adressen
      const [ticketResponse, usersResponse, milestonesData, adressenResponse] = await Promise.all([
        ticketsApi.getById(ticketId),
        benutzerApi.getMandantBenutzer(),
        milestoneService.getAll(),
        adressenApi.getAll({ take: 1000 }),
      ]);

      setSelectedTicket(ticketResponse.data);
      setTicketUsers(usersResponse.data);
      setTicketMilestones(milestonesData);
      setTicketAdressen(adressenResponse.data);
      setTicketDialogOpen(true);
    } catch (error: any) {
      console.error('Fehler beim Laden des Tickets:', error);
      setSnackbar({
        open: true,
        message: `Fehler beim Laden des Tickets: ${error.response?.data?.message || error.message}`,
        severity: 'error',
      });
    } finally {
      setTicketLoading(false);
    }
  };

  const handleCloseTicketDialog = () => {
    setTicketDialogOpen(false);
    setSelectedTicket(null);
  };

  const handleTicketSave = async (data: any) => {
    if (!selectedTicket) return;
    try {
      await ticketsApi.update(selectedTicket.ticketID, data);
      setSnackbar({
        open: true,
        message: 'Ticket erfolgreich aktualisiert!',
        severity: 'success',
      });
      handleCloseTicketDialog();
    } catch (error: any) {
      console.error('Fehler beim Speichern des Tickets:', error);
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error.response?.data?.message || error.message}`,
        severity: 'error',
      });
    }
  };

  const toggleCardExpand = (cardId: string) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const insertCommand = (template: string) => {
    setInput(template);
    // Focus auf das Eingabefeld setzen
    const inputElement = document.querySelector('input[type="text"], textarea') as HTMLElement;
    if (inputElement) {
      inputElement.focus();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'In Zwischenablage kopiert', severity: 'success' });
  };

  const handleImportAddress = async (address: AddressResult) => {
    try {
      // Parse name into first/last name
      const nameParts = (address.name || '').split(' ');
      const vorname = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : '';
      const nachname = nameParts.length > 0 ? nameParts[nameParts.length - 1] : address.name;

      const newAddress = {
        anrede: '',
        titel: '',
        vorname: vorname,
        nachname: nachname,
        zusatzName: '',
        firma: address.firma || '',
        email: address.email || '',
        telefon: address.telefon || '',
        strasse: address.strasse?.split(' ').slice(0, -1).join(' ') || address.strasse || '',
        hausnummer: address.strasse?.split(' ').pop() || '',
        zusatzadresse: '',
        plz: address.plz || '',
        postfach: '',
        ort: address.ort || '',
        land: address.land || 'Schweiz',
      };

      await adressenApi.create(newAddress);
      setSnackbar({ open: true, message: `Adresse "${address.name || address.firma}" erfolgreich importiert!`, severity: 'success' });
    } catch (error: any) {
      console.error('Import error:', error);
      setSnackbar({
        open: true,
        message: `Fehler beim Import: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validierung der Dateiformate
    const allowedExtensions = ['.docx', '.txt', '.md', '.odt', '.pdf'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      setSnackbar({
        open: true,
        message: `Ungültiges Dateiformat. Erlaubt: ${allowedExtensions.join(', ')}`,
        severity: 'error'
      });
      return;
    }

    setIsUploading(true);

    // Zeige Upload-Nachricht mit Animation
    const uploadingMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'assistant',
      content: '',
      messageType: 'document_uploading',
      data: { fileName: file.name },
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, uploadingMessage]);

    try {
      // 1. Datei hochladen
      const uploadResponse = await dokumenteApi.upload(file, file.name);
      const uploadedDocRaw = uploadResponse.data as any;
      console.log('Upload Response (raw):', uploadedDocRaw);

      // Backend gibt entweder { dokument: {...}, documentIntelligence: {...} } oder direkt das Dokument zurück
      const docData = uploadedDocRaw.dokument || uploadedDocRaw;
      const intelligenceData = uploadedDocRaw.documentIntelligence;
      console.log('Document data:', docData);
      console.log('Intelligence data:', intelligenceData);

      // Normalisiere PascalCase/camelCase
      const uploadedDoc = {
        dokumentID: docData.dokumentID || docData.DokumentID,
        betreff: docData.betreff || docData.Betreff,
        dokumentTyp: docData.dokumentTyp || docData.DokumentTyp,
        inhaltHtml: docData.inhaltHtml || docData.InhaltHtml,
        zusammenfassung: docData.zusammenfassung || docData.Zusammenfassung,
      };
      console.log('Upload Response (normalized):', uploadedDoc);

      // 2. Zusammenfassung - entweder vom Backend (Document Intelligence) oder aus dem Dokument
      let zusammenfassung = uploadedDoc.zusammenfassung || '';

      // 3. Antwortbrief - vom Backend über Document Intelligence automatisch erstellt
      let antwortBriefId: number | null = null;
      let antwortBriefBetreff = '';

      // Prüfe ob Document Intelligence einen Antwortbrief erstellt hat
      if (intelligenceData?.erstelltesDokumentId) {
        antwortBriefId = intelligenceData.erstelltesDokumentId;
        antwortBriefBetreff = `Antwort: ${uploadedDoc.betreff || file.name}`;
        console.log('Antwortbrief von Document Intelligence:', antwortBriefId);
      } else {
        console.log('Kein Antwortbrief von Document Intelligence erstellt');
      }

      // 4. Erfolgsnachricht mit Zusammenfassung und Antwortbrief
      const messageData = {
        dokumentId: uploadedDoc.dokumentID,
        betreff: uploadedDoc.betreff || file.name,
        dokumentTyp: uploadedDoc.dokumentTyp,
        zusammenfassung: zusammenfassung,
        hasContent: !!uploadedDoc.inhaltHtml,
        antwortBriefId: antwortBriefId,
        antwortBriefBetreff: antwortBriefBetreff,
      };
      console.log('Message data für document_uploaded:', messageData);

      const successMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `**Dokument erfolgreich hochgeladen!**\n\n**Datei:** ${file.name}`,
        messageType: 'document_uploaded',
        data: messageData,
        timestamp: new Date(),
      };

      // Entferne die "Lade hoch" Nachricht und füge Erfolgsnachricht hinzu
      setMessages((prev) => {
        const filtered = prev.filter(m => m.id !== uploadingMessage.id);
        return [...filtered, successMessage];
      });

      setSnackbar({
        open: true,
        message: 'Dokument erfolgreich hochgeladen!',
        severity: 'success'
      });

    } catch (error: any) {
      console.error('Upload-Fehler:', error);

      // Entferne die "Lade hoch" Nachricht und zeige Fehler
      setMessages((prev) => {
        const filtered = prev.filter(m => m.id !== uploadingMessage.id);
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `**Fehler beim Upload:** ${error.response?.data?.message || error.message}`,
          messageType: 'error',
          timestamp: new Date(),
        };
        return [...filtered, errorMessage];
      });

      setSnackbar({
        open: true,
        message: `Upload-Fehler: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const renderAddressResults = (data: { results: AddressResult[]; query: string; source?: string }) => {
    const isExternal = data.source === 'external';

    return (
      <Box sx={{ mt: 1 }}>
        {data.results.map((address, index) => (
          <Card
            key={address.id || `addr-${index}`}
            sx={{
              mb: 1,
              backgroundColor: isExternal ? 'info.50' : 'background.default',
              borderLeft: isExternal ? '3px solid' : 'none',
              borderColor: isExternal ? 'info.main' : 'transparent',
              '&:hover': { boxShadow: 2 },
            }}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {address.firma ? (
                      <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    ) : (
                      <UserIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    )}
                    <Typography variant="subtitle1" fontWeight="bold">
                      {address.firma || address.name}
                    </Typography>
                    {isExternal && (
                      <Tooltip title={`Quelle: ${address.source || 'tel.search.ch'}`}>
                        <Chip
                          icon={<WebIcon sx={{ fontSize: 14 }} />}
                          label="extern"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                        />
                      </Tooltip>
                    )}
                    {!isExternal && address.isInternal && (
                      <Tooltip title="Aus Ihrer Datenbank">
                        <Chip
                          icon={<InternalIcon sx={{ fontSize: 14 }} />}
                          label="intern"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  {address.firma && address.name && (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                      {address.name}
                    </Typography>
                  )}
                  {/* Kurzinfo direkt sichtbar */}
                  {(address.strasse || address.ort) && (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5, fontSize: '0.85rem' }}>
                      {address.strasse}{address.strasse && address.ort ? ', ' : ''}{address.plz} {address.ort}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {isExternal && (
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      startIcon={<ImportIcon />}
                      onClick={() => handleImportAddress(address)}
                      sx={{
                        whiteSpace: 'nowrap',
                        fontSize: '0.75rem',
                        py: 0.5,
                        px: 1.5
                      }}
                    >
                      Ja, hinzufügen
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => toggleCardExpand(`addr-${address.id || index}`)}
                  >
                    {expandedCards[`addr-${address.id || index}`] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={expandedCards[`addr-${address.id || index}`]}>
                <Divider sx={{ my: 1 }} />
                <List dense disablePadding>
                  {(address.strasse || address.plz || address.ort) && (
                    <ListItem disablePadding sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <LocationIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${address.strasse || ''}${address.strasse ? ', ' : ''}${address.plz || ''} ${address.ort || ''}${address.land && address.land !== 'Schweiz' ? `, ${address.land}` : ''}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(`${address.strasse || ''}, ${address.plz || ''} ${address.ort || ''}`)}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  )}
                  {address.telefon && (
                    <ListItem disablePadding sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <PhoneIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={address.telefon}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <IconButton size="small" onClick={() => copyToClipboard(address.telefon!)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  )}
                  {address.email && (
                    <ListItem disablePadding sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <EmailIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={address.email}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <IconButton size="small" onClick={() => copyToClipboard(address.email!)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  )}
                </List>
              </Collapse>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  const renderMessage = (message: ChatMessage) => {
    const isUser = message.role === 'user';

    return (
      <Box
        key={message.id}
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isUser ? 'row-reverse' : 'row',
            alignItems: 'flex-start',
            maxWidth: '80%',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isUser ? 'primary.main' : 'secondary.main',
              color: 'white',
              mx: 1,
            }}
          >
            {isUser ? <UserIcon fontSize="small" /> : <AIIcon fontSize="small" />}
          </Box>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              backgroundColor: isUser ? 'primary.light' : 'background.paper',
              borderRadius: 2,
              borderTopLeftRadius: isUser ? 2 : 0,
              borderTopRightRadius: isUser ? 0 : 2,
            }}
          >
            <Typography
              variant="body1"
              sx={{ whiteSpace: 'pre-wrap' }}
            >
              {message.content}
            </Typography>

            {message.messageType === 'address_results' && message.data && (
              renderAddressResults(message.data)
            )}

            {message.messageType === 'external_address_results' && message.data && (
              <>
                <Alert severity="info" sx={{ mt: 1, mb: 1 }} icon={<ExternalIcon />}>
                  Externe Suchergebnisse von {message.data.results?.[0]?.source || 'tel.search.ch'}
                </Alert>
                {renderAddressResults(message.data)}
              </>
            )}

            {message.messageType === 'ticket_created' && message.data && (
              <Card sx={{ mt: 1, backgroundColor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SuccessIcon sx={{ color: 'success.main' }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Ticket #{message.data.ticketId}
                    </Typography>
                    <Chip
                      label={message.data.prioritaet}
                      size="small"
                      color={
                        message.data.prioritaet === 'Kritisch' ? 'error' :
                        message.data.prioritaet === 'Hoch' ? 'warning' :
                        message.data.prioritaet === 'Niedrig' ? 'default' : 'info'
                      }
                      icon={<PriorityIcon sx={{ fontSize: 14 }} />}
                      sx={{ height: 22 }}
                    />
                  </Box>
                  <Box sx={{ ml: 4 }}>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 0.5 }}>
                      {message.data.titel}
                    </Typography>
                    {message.data.was && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {message.data.was}
                      </Typography>
                    )}
                    {message.data.wannFormatted && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Fällig: {message.data.wannFormatted}
                        </Typography>
                      </Box>
                    )}
                    {message.data.adresseName && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {message.data.adresseName}
                        </Typography>
                      </Box>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={ticketLoading ? <CircularProgress size={16} /> : <OpenInNewIcon />}
                      sx={{ mt: 1.5 }}
                      onClick={() => handleOpenTicketDialog(message.data.ticketId)}
                      disabled={ticketLoading}
                    >
                      {ticketLoading ? 'Lädt...' : 'Ticket öffnen'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Dokumenten-Suchergebnisse */}
            {message.messageType === 'document_results' && message.data && (
              <Box sx={{ mt: 1 }}>
                {message.data.results?.map((doc: any, index: number) => {
                  const isPaperless = doc.source === 'paperless';
                  const isMeilisearch = doc.source === 'meilisearch';
                  return (
                  <Card
                    key={index}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                      borderLeft: '3px solid',
                      borderColor: isPaperless ? 'warning.main' :
                                   doc.dokumentTyp === 'Brief' ? 'primary.main' :
                                   doc.dokumentTyp === 'Email' ? 'info.main' :
                                   doc.dokumentTyp === 'Angebot' ? 'success.main' : 'grey.400',
                    }}
                    onClick={() => {
                      if (isPaperless) {
                        // Paperless-Dokumente öffnen die Paperless-Seite mit Suchfilter
                        navigate(`/paperless?search=${encodeURIComponent(doc.betreff)}`);
                      } else {
                        // Lokale Dokumente öffnen die Dokumentendetailseite
                        navigate(`/dokumente/${doc.dokumentId}`);
                      }
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        {isPaperless ? (
                          <ArchiveIcon sx={{ color: 'warning.main', mt: 0.5 }} />
                        ) : (
                          <DocumentIcon sx={{ color: 'text.secondary', mt: 0.5 }} />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" noWrap>
                            {doc.betreff || 'Ohne Betreff'}
                            {isMeilisearch && (
                              <Typography component="span" variant="caption" color="info.main" sx={{ ml: 1 }}>
                                (ähnlich)
                              </Typography>
                            )}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label={isPaperless ? 'Paperless' : doc.dokumentTyp}
                              size="small"
                              color={isPaperless ? 'warning' :
                                     doc.dokumentTyp === 'Brief' ? 'primary' :
                                     doc.dokumentTyp === 'Email' ? 'info' :
                                     doc.dokumentTyp === 'Angebot' ? 'success' : 'default'}
                              variant={isPaperless ? 'filled' : 'outlined'}
                            />
                            {!isPaperless && (
                              <Chip
                                label={doc.status}
                                size="small"
                                variant="outlined"
                                color={doc.status === 'Versendet' ? 'success' :
                                       doc.status === 'Entwurf' ? 'warning' : 'default'}
                              />
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {doc.erstelltAm}
                            </Typography>
                          </Box>
                          {/* Aktionsbuttons */}
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<MailIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isPaperless) {
                                  // Paperless-Dokument: Öffne Paperless zum Download
                                  setSnackbar({
                                    open: true,
                                    message: 'Öffne Paperless - bitte Dokument herunterladen und manuell anhängen.',
                                    severity: 'success',
                                  });
                                  navigate(`/paperless?search=${encodeURIComponent(doc.betreff)}`);
                                } else {
                                  // Lokales Dokument: Öffne E-Mail-Compose mit Anhang
                                  navigate(`/emails/compose/new?dokumentId=${doc.dokumentId}&betreff=${encodeURIComponent(doc.betreff)}`);
                                }
                              }}
                              sx={{ fontSize: '0.75rem' }}
                            >
                              Als E-Mail versenden
                            </Button>
                          </Box>
                        </Box>
                        <OpenInNewIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                      </Box>
                    </CardContent>
                  </Card>
                );
                })}
              </Box>
            )}

            {/* Keine Dokumente gefunden */}
            {message.messageType === 'document_no_results' && (
              <Alert severity="info" sx={{ mt: 1 }} icon={<DocumentIcon />}>
                Keine Dokumente gefunden. Versuchen Sie einen anderen Suchbegriff.
              </Alert>
            )}

            {/* E-Mail-Suchergebnisse */}
            {message.messageType === 'email_results' && message.data && (
              <Box sx={{ mt: 1 }}>
                {message.data.results?.map((email: any, index: number) => (
                  <Card
                    key={index}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                      borderLeft: '3px solid',
                      borderColor: email.gelesen ? 'grey.400' : 'primary.main',
                      backgroundColor: email.gelesen ? 'background.default' : 'primary.50',
                    }}
                    onClick={() => navigate(`/emails?id=${email.emailId}`)}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        {email.gelesen ? (
                          <ReadIcon sx={{ color: 'grey.500', mt: 0.5 }} />
                        ) : (
                          <UnreadIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={email.gelesen ? 'normal' : 'bold'}
                              noWrap
                              sx={{ flex: 1 }}
                            >
                              {email.betreff || '(Kein Betreff)'}
                            </Typography>
                            {email.wichtig && (
                              <StarIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                            )}
                            {email.hasAttachments && (
                              <AttachmentIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {email.absenderAnzeige}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {email.empfangenAm}
                            </Typography>
                            {email.kategorie && (
                              <Chip
                                label={email.kategorie}
                                size="small"
                                variant="outlined"
                                sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                              />
                            )}
                            {email.beantwortet && (
                              <Chip
                                icon={<ReplyIcon sx={{ fontSize: 12 }} />}
                                label="Beantwortet"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                              />
                            )}
                          </Box>
                          {email.zusammenfassung && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mt: 0.5,
                                fontStyle: 'italic'
                              }}
                            >
                              {email.zusammenfassung}
                            </Typography>
                          )}
                        </Box>
                        <OpenInNewIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {/* Keine E-Mails gefunden */}
            {message.messageType === 'email_no_results' && (
              <Alert severity="info" sx={{ mt: 1 }} icon={<EmailIcon />}>
                Keine E-Mails gefunden. Versuchen Sie einen größeren Zeitraum oder andere Suchkriterien.
              </Alert>
            )}

            {/* Dokumentenerstellung Weiterleitung */}
            {message.messageType === 'document_create_redirect' && message.data && (
              <Card sx={{ mt: 1, backgroundColor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ArticleIcon sx={{ color: 'info.main' }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Dokument erstellen: {message.data.dokumentTyp}
                    </Typography>
                  </Box>

                  {message.data.gefundeneAdresse && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <BusinessIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="body2" color="success.main">
                        Empfänger: {message.data.gefundeneAdresse}
                      </Typography>
                    </Box>
                  )}

                  {message.data.betreff && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Betreff: {message.data.betreff}
                    </Typography>
                  )}

                  {message.data.inhaltKontext && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Kontext: {message.data.inhaltKontext}
                    </Typography>
                  )}

                  {message.data.vorlageName && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Vorlage: {message.data.vorlageName}
                    </Typography>
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    sx={{ mt: 1 }}
                    onClick={() => {
                      // Verwende die vom Backend generierte URL mit allen Parametern
                      if (message.data.createUrl) {
                        navigate(message.data.createUrl);
                      } else {
                        // Fallback: Baue URL manuell
                        const params = new URLSearchParams();
                        if (message.data.dokumentTyp) params.set('typ', message.data.dokumentTyp);
                        if (message.data.adresseId) params.set('adresseId', message.data.adresseId);
                        if (message.data.betreff) params.set('betreff', message.data.betreff);
                        if (message.data.inhaltKontext) params.set('inhalt', message.data.inhaltKontext);
                        if (message.data.vorlageName) params.set('vorlage', message.data.vorlageName);
                        navigate(`/dokumente/neu?${params.toString()}`);
                      }
                    }}
                  >
                    Dokument erstellen
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Dokument-Upload Aufforderung */}
            {message.messageType === 'document_upload_request' && (
              <Card sx={{ mt: 1, backgroundColor: 'primary.50', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CloudUploadIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Dokument hochladen
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Unterstützte Formate: .docx, .txt, .md, .odt, .pdf
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    sx={{ mt: 1 }}
                  >
                    {isUploading ? 'Wird hochgeladen...' : 'Datei auswählen'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Dokument wird hochgeladen - Animierte Karte */}
            {message.messageType === 'document_uploading' && message.data && (
              <Card
                sx={{
                  mt: 1,
                  backgroundColor: 'info.50',
                  borderLeft: '4px solid',
                  borderColor: 'info.main',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.7 },
                    '100%': { opacity: 1 },
                  },
                }}
              >
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <CircularProgress size={24} color="info" />
                    <Typography variant="subtitle1" fontWeight="bold" color="info.main">
                      Bitte warten - Datei wird verarbeitet...
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <DocumentIcon sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {message.data.fileName}
                    </Typography>
                  </Box>

                  <LinearProgress
                    color="info"
                    sx={{
                      borderRadius: 1,
                      height: 6,
                    }}
                  />

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                    Dokument wird hochgeladen und analysiert...
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Dokument erfolgreich hochgeladen */}
            {message.messageType === 'document_uploaded' && message.data && (
              <Card sx={{ mt: 1, backgroundColor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SuccessIcon sx={{ color: 'success.main' }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Dokument erfolgreich hochgeladen!
                    </Typography>
                  </Box>

                  {/* Hochgeladenes Dokument als klickbare Karte */}
                  <Card
                    sx={{
                      mb: 2,
                      cursor: 'pointer',
                      backgroundColor: 'background.paper',
                      '&:hover': { backgroundColor: 'action.hover', boxShadow: 2 },
                      borderLeft: '3px solid',
                      borderColor: 'primary.main',
                    }}
                    onClick={() => navigate(`/dokumente/${message.data.dokumentId}`)}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DocumentIcon sx={{ color: 'primary.main' }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight="bold" noWrap>
                            {message.data.betreff}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                            <Chip
                              label={message.data.dokumentTyp || 'Dokument'}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              Hochgeladen
                            </Typography>
                          </Box>
                        </Box>
                        <OpenInNewIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                      </Box>
                    </CardContent>
                  </Card>

                  {/* KI-Zusammenfassung */}
                  {message.data.zusammenfassung && (
                    <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="primary.main" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AIIcon sx={{ fontSize: 16 }} /> KI-Zusammenfassung
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.data.zusammenfassung}
                      </Typography>
                    </Box>
                  )}

                  {/* Antwortbrief-Bereich */}
                  <Divider sx={{ my: 1.5 }} />

                  {/* Wenn Antwortbrief generiert wurde */}
                  {message.data.antwortBriefId ? (
                    <>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Antwortbrief wurde automatisch erstellt:
                      </Typography>

                      {/* Generierter Antwortbrief als klickbare Karte */}
                      <Card
                        sx={{
                          mb: 1,
                          cursor: 'pointer',
                          backgroundColor: 'background.paper',
                          '&:hover': { backgroundColor: 'action.hover', boxShadow: 2 },
                          borderLeft: '3px solid',
                          borderColor: 'secondary.main',
                        }}
                        onClick={() => navigate(`/dokumente/${message.data.antwortBriefId}`)}
                      >
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ReplyIcon sx={{ color: 'secondary.main' }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                {message.data.antwortBriefBetreff || `Antwort: ${message.data.betreff}`}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                                <Chip
                                  label="Brief"
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                />
                                <Typography variant="caption" color="text.secondary">
                                  Antwortbrief
                                </Typography>
                              </Box>
                            </Box>
                            <OpenInNewIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                          </Box>
                        </CardContent>
                      </Card>
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Möchten Sie auf dieses Dokument antworten?
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<ReplyIcon />}
                        onClick={() => {
                          // Antwortbrief erstellen - mit Kontext des hochgeladenen Dokuments
                          // Öffnet in neuem Tab, damit der Chat erhalten bleibt
                          const params = new URLSearchParams();
                          params.set('typ', 'Brief');
                          if (message.data.zusammenfassung) {
                            params.set('inhalt', `Antwort auf: ${message.data.betreff}\n\n${message.data.zusammenfassung}`);
                          }
                          params.set('betreff', `Antwort: ${message.data.betreff}`);
                          window.open(`/dokumente/neu?${params.toString()}`, '_blank');
                        }}
                      >
                        Antwortbrief erstellen
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {message.messageType === 'no_results' && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Keine Ergebnisse gefunden - weder intern noch extern.
              </Alert>
            )}

            {message.messageType === 'error' && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {message.content}
              </Alert>
            )}

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1, textAlign: isUser ? 'left' : 'right' }}
            >
              {message.timestamp.toLocaleTimeString('de-CH', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          </Paper>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={0} sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AIIcon color="primary" />
            <Typography variant="h6">Skeena AI Chat</Typography>
            <Chip label="Beta" size="small" color="info" sx={{ ml: 1 }} />
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<NewChatIcon />}
            onClick={handleNewChat}
            disabled={isLoading}
          >
            Neuer Chat
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Stellen Sie Fragen in natürlicher Sprache - Suche erfolgt intern und extern (tel.search.ch)
        </Typography>
      </Paper>

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Chat-Bereich */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            backgroundColor: 'grey.50',
          }}
        >
          {messages.map(renderMessage)}
          {isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 6 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Suche intern und extern...
              </Typography>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Befehls-Sidebar */}
        <Paper
          elevation={0}
          sx={{
            width: 280,
            borderLeft: 1,
            borderColor: 'divider',
            overflow: 'auto',
            display: { xs: 'none', md: 'block' },
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', backgroundColor: 'primary.50' }}>
            <Typography variant="subtitle2" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <HelpIcon fontSize="small" />
              Verfügbare Befehle
            </Typography>
          </Box>
          <List dense disablePadding>
            {COMMAND_CATEGORIES.map((category) => (
              <React.Fragment key={category.title}>
                <ListItem
                  component="div"
                  onClick={() => toggleCategory(category.title)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: 'grey.100',
                    '&:hover': { backgroundColor: 'grey.200' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {category.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={category.title}
                    primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'bold' }}
                  />
                  {expandedCategories[category.title] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </ListItem>
                <Collapse in={expandedCategories[category.title]}>
                  <List dense disablePadding>
                    {category.commands.map((cmd, idx) => (
                      <ListItem
                        key={idx}
                        component="div"
                        onClick={() => insertCommand(cmd.template)}
                        sx={{
                          pl: 4,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'primary.50' },
                        }}
                      >
                        <ListItemText
                          primary={cmd.label}
                          secondary={cmd.description}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Box>

      <Paper elevation={3} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={isRecording ? "Sprechen Sie jetzt..." : isTranscribing ? "Wird transkribiert..." : "Stellen Sie eine Frage... (z.B. 'Zeige mir die Adresse von...')"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading || isTranscribing}
            size="small"
            multiline
            maxRows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderColor: isRecording ? 'error.main' : isTranscribing ? 'info.main' : undefined,
                animation: isRecording ? 'pulse 1.5s ease-in-out infinite' : 'none',
              },
              '@keyframes pulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
                '70%': { boxShadow: '0 0 0 6px rgba(211, 47, 47, 0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
              },
            }}
          />
          {speechSupported && (
            <Tooltip title={isRecording ? "Aufnahme stoppen" : isTranscribing ? "Transkription läuft..." : `Spracheingabe starten (${speechMode === 'whisper' ? 'Whisper' : 'Browser'})`}>
              <span>
                <IconButton
                  color={isRecording ? "error" : isTranscribing ? "info" : "default"}
                  onClick={toggleRecording}
                  disabled={isLoading || isTranscribing}
                  sx={{
                    backgroundColor: isRecording ? 'error.main' : isTranscribing ? 'info.main' : 'grey.200',
                    color: isRecording || isTranscribing ? 'white' : 'grey.700',
                    '&:hover': {
                      backgroundColor: isRecording ? 'error.dark' : isTranscribing ? 'info.dark' : 'grey.300',
                    },
                    animation: isRecording ? 'pulse-mic 1s ease-in-out infinite' : isTranscribing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes pulse-mic': {
                      '0%, 100%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.1)' },
                    },
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                >
                  {isTranscribing ? <CircularProgress size={20} color="inherit" /> : isRecording ? <MicOffIcon /> : <MicIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': { backgroundColor: 'primary.dark' },
              '&:disabled': { backgroundColor: 'grey.300' },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
        {(isRecording || isTranscribing) && (
          <Typography variant="caption" color={isRecording ? "error" : "info.main"} sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
            {isRecording
              ? `🎤 Aufnahme läuft (${speechMode === 'whisper' ? 'Whisper' : 'Browser'})... Sprechen Sie jetzt.`
              : '⏳ Transkription läuft... Bitte warten.'}
          </Typography>
        )}
      </Paper>

      {/* Hidden file input for document upload */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".docx,.txt,.md,.odt,.pdf"
        onChange={handleFileUpload}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* Ticket Dialog */}
      <TicketDialog
        open={ticketDialogOpen}
        ticket={selectedTicket}
        users={ticketUsers}
        adressen={ticketAdressen}
        milestones={ticketMilestones}
        onClose={handleCloseTicketDialog}
        onSave={handleTicketSave}
      />
    </Box>
  );
};

export default AIChat;
